import { useState, useMemo } from 'react';
import { useAuditLog, useCenters } from '@hooks/useAdminResources';
import { useActiveCenter, ALL_CENTERS } from '@hooks/useActiveCenter';
import { useAuth } from '@hooks/useAuth';
import type { AuditLogFilters } from '@api/types';
import * as Icons from '@components/Icons';
import { PageHead } from '@components/admin/PageHead';

const MONTHS_ES = ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'];

type Tab = 'sistema' | 'reservas';

const BOOKING_ACTIONS = [
  'booking.created',
  'booking.updated',
  'booking.cancelled',
  'booking.rescheduled',
  'booking.deleted',
] as const;

function fmtDateTime(iso: string) {
  if (!iso) return '—';
  const d = new Date(iso);
  const day = String(d.getDate());
  const mon = MONTHS_ES[d.getMonth()] ?? '';
  const hr = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${day} ${mon} ${String(d.getFullYear())} · ${hr}:${min}`;
}

function actionTone(action: string): string {
  if (action.startsWith('delete') || action.includes('cancel') || action.includes('erase')) return 'danger';
  if (action.startsWith('create') || action.includes('confirm')) return 'ok';
  if (action.startsWith('update') || action.includes('reschedule')) return 'info';
  return 'neutral';
}

export function HistorialPage() {
  const { user } = useAuth();
  // Sistema = cross-resource system audit. Reservas = booking-only timeline.
  // Only superadmin gets the Sistema tab; center_admin lands directly on
  // Reservas (scoped to their centre by the route layer's JWT-derived centerId).
  const isSuperadmin = user?.role === 'superadmin';
  const [tab, setTab] = useState<Tab>(isSuperadmin ? 'sistema' : 'reservas');
  const [filters, setFilters] = useState<AuditLogFilters>({ page: 1, perPage: 20 });
  const [q, setQ] = useState('');

  // Shared centre filter — `'all'` becomes undefined for the AuditLog query
  // (backend fans out across the user's allowed set).
  const { activeCenterId, setActiveCenterId } = useActiveCenter();

  // Superadmin-only centre dropdown (only visible inside the Reservas tab,
  // where narrowing by centre is the common need). The list endpoint returns
  // 403 for non-superadmin so we gate the fetch.
  const { data: centers = [] } = useCenters(isSuperadmin);

  // The Reservas tab pins entityType=Booking server-side (the entity type is
  // stored capitalized by every use case — see `application/booking/*`), and
  // the action dropdown is limited to the five booking actions. Filters in
  // state still hold the user's choice; we merge in `entityType` for the hook.
  const sharedCenterId = activeCenterId === ALL_CENTERS ? undefined : activeCenterId;
  const effectiveFilters: AuditLogFilters = tab === 'reservas'
    ? { ...filters, entityType: 'Booking', centerId: sharedCenterId }
    : { ...filters, centerId: sharedCenterId };

  function switchTab(next: Tab) {
    if (next === tab) return;
    setTab(next);
    // Reset the action / centre filters when switching tabs: the option lists
    // differ and the centre dropdown only applies to Reservas.
    setFilters((f) => ({ ...f, action: undefined, centerId: undefined, page: 1 }));
    setQ('');
  }

  const { data, isLoading } = useAuditLog(effectiveFilters);
  const entries = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const page = filters.page ?? 1;

  const filtered = useMemo(() => {
    if (!q) return entries;
    const s = q.toLowerCase();
    return entries.filter(
      (e) =>
        e.action.toLowerCase().includes(s) ||
        e.resourceType.toLowerCase().includes(s) ||
        e.resourceId.toLowerCase().includes(s) ||
        (e.actorName ?? '').toLowerCase().includes(s) ||
        (e.actorEmail ?? '').toLowerCase().includes(s) ||
        (e.userId ?? '').toLowerCase().includes(s),
    );
  }, [entries, q]);

  return (
    <div>
      <PageHead
        title="Historial"
        sub={
          tab === 'reservas'
            ? 'Historial de cambios sobre reservas. Retención: 90 días.'
            : 'Registro de acciones del sistema. Retención: 90 días.'
        }
        meta={<span className="mono">{total} eventos</span>}
      />

      {isSuperadmin && (
        <div className="toolbar" style={{ marginBottom: 10 }}>
          <button
            type="button"
            className={`pill${tab === 'sistema' ? ' is-active' : ''}`}
            data-tone={tab === 'sistema' ? 'info' : undefined}
            onClick={() => { switchTab('sistema'); }}
            data-testid="tab-sistema"
          >
            Sistema
          </button>
          <button
            type="button"
            className={`pill${tab === 'reservas' ? ' is-active' : ''}`}
            data-tone={tab === 'reservas' ? 'info' : undefined}
            onClick={() => { switchTab('reservas'); }}
            data-testid="tab-reservas"
          >
            Reservas
          </button>
        </div>
      )}

      <div className="toolbar">
        <div className="search">
          <Icons.Search size={14} className="ico" />
          <input
            className="input"
            placeholder="Buscar por acción, entidad o actor…"
            value={q}
            onChange={(e) => { setQ(e.target.value); }}
          />
        </div>
        <select
          className="select"
          style={{ width: 'auto' }}
          value={filters.action ?? ''}
          onChange={(e) => { setFilters((f) => ({ ...f, action: e.target.value || undefined, page: 1 })); }}
        >
          <option value="">Cualquier acción</option>
          {tab === 'reservas' ? (
            BOOKING_ACTIONS.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))
          ) : (
            <>
              <optgroup label="Reservas">
                <option value="booking.created">booking.created</option>
                <option value="booking.updated">booking.updated</option>
                <option value="booking.cancelled">booking.cancelled</option>
                <option value="booking.rescheduled">booking.rescheduled</option>
                <option value="booking.deleted">booking.deleted</option>
              </optgroup>
              <optgroup label="Clientes">
                <option value="customer.updated">customer.updated</option>
                <option value="customer.deleted">customer.deleted</option>
                <option value="customer.merged">customer.merged</option>
              </optgroup>
              <optgroup label="Servicios">
                <option value="service.created">service.created</option>
                <option value="service.updated">service.updated</option>
                <option value="service.deleted">service.deleted</option>
                <option value="service.rooms.set">service.rooms.set</option>
                <option value="service.staff.set">service.staff.set</option>
              </optgroup>
              <optgroup label="Staff">
                <option value="staff.created">staff.created</option>
                <option value="staff.updated">staff.updated</option>
                <option value="staff.deleted">staff.deleted</option>
                <option value="staff.assigned_to_center">staff.assigned_to_center</option>
                <option value="staff.removed_from_center">staff.removed_from_center</option>
              </optgroup>
              <optgroup label="Centros">
                <option value="center.created">center.created</option>
                <option value="center.updated">center.updated</option>
                <option value="center.deleted">center.deleted</option>
              </optgroup>
              <optgroup label="Salas">
                <option value="room.created">room.created</option>
                <option value="room.updated">room.updated</option>
                <option value="room.deleted">room.deleted</option>
              </optgroup>
              <optgroup label="Autenticación / RGPD">
                <option value="auth.magic_link_requested">auth.magic_link_requested</option>
                <option value="gdpr.data_exported">gdpr.data_exported</option>
                <option value="gdpr.customer_erased">gdpr.customer_erased</option>
              </optgroup>
            </>
          )}
        </select>
        {isSuperadmin && tab === 'reservas' && (
          <select
            className="select"
            style={{ width: 'auto' }}
            value={activeCenterId}
            onChange={(e) => { setActiveCenterId(e.target.value); setFilters((f) => ({ ...f, page: 1 })); }}
            data-testid="centre-filter"
          >
            <option value={ALL_CENTERS}>Todos los centros</option>
            {centers.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        )}
        <div className="toolbar-spacer" />
        <span className="mono" style={{ fontSize: 11.5, color: 'var(--fg-soft)' }}>
          {filtered.length} resultados
        </span>
      </div>

      <div className="card">
        <table className="table" data-testid="audit-log-table">
          <thead>
            <tr>
              <th>Cuándo</th>
              <th>Acción</th>
              <th>Entidad</th>
              <th>Actor</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={4} style={{ textAlign: 'center', padding: 32, color: 'var(--fg-muted)' }}>Cargando…</td></tr>
            )}
            {!isLoading && filtered.length === 0 && (
              <tr><td colSpan={4} style={{ textAlign: 'center', padding: 32, color: 'var(--fg-muted)' }}>Sin entradas</td></tr>
            )}
            {filtered.map((entry) => (
              <tr key={entry.id} data-testid={`audit-row-${entry.id}`}>
                <td className="mono cell-muted" style={{ fontSize: 12 }}>{fmtDateTime(entry.createdAt)}</td>
                <td>
                  <span className="pill" data-tone={actionTone(entry.action)} style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                    {entry.action}
                  </span>
                </td>
                <td>
                  <div style={{ fontSize: 13 }}>{entry.resourceType}</div>
                  <div className="cell-muted mono" style={{ fontSize: 11 }}>{entry.resourceId.slice(0, 14)}</div>
                </td>
                <td className="cell-muted" style={{ fontSize: 12 }}>
                  {entry.actorName ?? entry.actorEmail ?? entry.userId ?? '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {totalPages > 1 && (
          <div className="card-foot">
            <span>Página {page} de {totalPages} · {total} total</span>
            <div style={{ display: 'flex', gap: 4 }}>
              <button
                className="icon-btn"
                disabled={page <= 1}
                data-testid="audit-prev"
                onClick={() => { setFilters((f) => ({ ...f, page: (f.page ?? 1) - 1 })); }}
              >
                <Icons.ChevL size={14} />
              </button>
              <button
                className="icon-btn"
                disabled={page >= totalPages}
                data-testid="audit-next"
                onClick={() => { setFilters((f) => ({ ...f, page: (f.page ?? 1) + 1 })); }}
              >
                <Icons.ChevR size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
